import React from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import {ErrorBoundary} from 'react-error-boundary';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {getEvBatteryPerformanceStatus} from '@di-shared/features/home/kr/ev-battery/util';
import {EvBatteryPerformanceStatus} from '@di-shared/features/home/kr/ev-battery/monitor/const';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {emitEventLog} from '@di-shared/features/analytics';

interface EvBatteryPerformanceStatusProps {
  vin: string;
  puid: string;
}

function EvBatteryPerformance(props: EvBatteryPerformanceStatusProps) {
  return (
    <ErrorBoundary fallback={<NoData />}>
      <Inner {...props} />
    </ErrorBoundary>
  );
}

function Inner({vin, puid}: EvBatteryPerformanceStatusProps) {
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.evBatteryController.getEvBatteryStatus), vin, puid],
    queryFn: () => diApi.evBatteryController.getEvBatteryStatus({vin, puid}),
  });

  const evBatteryPerformanceStatus = getEvBatteryPerformanceStatus(data.soh);

  switch (evBatteryPerformanceStatus) {
    case EvBatteryPerformanceStatus.GOOD:
      return <Good />;
    case EvBatteryPerformanceStatus.NORMAL:
      return <Normal />;
    case EvBatteryPerformanceStatus.ERROR:
    default:
      return <NoData />;
  }
}

function Good() {
  const {t} = useInferableTranslation();
  return (
    <div className="battery_status_box">
      <a
        role={'button'}
        className="battery_status_link"
        onClick={async () => {
          emitEventLog({
            event: 'vehicle_status_check',
            interaction_type: 'ev_soh_status',
          });
          await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_PERFORMANCE.getFullPath(), {
            evBatteryPerformanceStatus: EvBatteryPerformanceStatus.GOOD,
          });
        }}
      >
        <span className="ic ic_filled_good_face"></span>
        <div className="battery_info">
          <p className="battery_status">{t('T30453')}</p>
        </div>
        <span className="battery_title">{t('T30454')}</span>
      </a>
    </div>
  );
}

function Normal() {
  const {t} = useInferableTranslation();
  return (
    <div className="battery_status_box">
      <a
        role={'button'}
        className="battery_status_link"
        onClick={async () => {
          emitEventLog({
            event: 'vehicle_status_check',
            interaction_type: 'ev_soh_status',
          });
          await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_PERFORMANCE.getFullPath(), {
            evBatteryPerformanceStatus: EvBatteryPerformanceStatus.NORMAL,
          });
        }}
      >
        <span className="ic ic_filled_normal_face"></span>
        <div className="battery_info">
          <p className="battery_status">{t('T30490')}</p>
        </div>
        <span className="battery_title">{t('T30454')}</span>
      </a>
    </div>
  );
}

function NoData() {
  const {t} = useInferableTranslation();
  return (
    <div className="battery_status_box">
      <a className="battery_status_link">
        <div className="loading_lottie">
          <LottieSelector
            id="img_json_loading_black_60f"
            kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
            kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
            genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
            genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
            loop={true}
            autoplay={true}
            style={{width: 24, height: 24}}
          />
        </div>
        <div className="battery_info">
          <p className="battery_status">{t('T20082')}</p>
        </div>
        <span className="battery_title">{t('T30454')}</span>
      </a>
    </div>
  );
}

export default EvBatteryPerformance;
